import React, { FC, useState, useEffect, Fragment } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';

import { PlayerPoints, TeamGameMatchDataDisplay } from '../../../../database.schema';
import { isLoggedIn } from '../../../app/authentication/auth';
import { CentreHeading, FlexColumnCentredNoMargin, FlexRowSpaceBetween, SectionWrapper } from '../../common-styles/common-styles';
import { useFetchTeamGameResultsByDay } from '../../../api/hooks/use-fetch-team-totals-by-day';
import { MATCH_FORMATS } from '../../../app/constants';
import { TeamGameMatchResult } from './team-game-match-result';
import { MatchResult } from '../ryderCup/match-result';
import { TeamGameStablefordResult } from './team-game-stableford-result';

const PageWrapper = styled.div`
  margin-right: 2rem;
  display: flex;
  justify-content: space-evenly;
  flex-direction: column;
  &h3: {
    margin: 0;
  }
`;
const IndividualWrapper = styled.div`
  display: flex;
  justify-content: space-around;
  flex-direction: column;
`;
const PlayerWrapper = styled.div`
  display: flex;
  justify-content: center;
`;
const Position = styled.div`
  width: 4rem;
  text-align: right;
  margin-right: 1rem;
`;

const Points = styled.div`
  width: 6rem;
  text-align: center;
`;
const LdNtp = styled.div`
  width: 6rem;
  text-align: center;
`;

const LdNtpSymbol = styled(LdNtp)`
  font-size: 12px;
`;

const Player = styled.div`
  width: 6rem;
  text-align: left;
`;

const Title = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 0.5rem 0.5rem 0.25rem 0;
  div {
    h3 {
      font-size: 20px;
    }
  }
`;
const Button = styled.button`
  border-radius: 5px;
  margin-left: 1rem;
  background: lightblue;
  border: 1px solid #5c9db2;
  font-weight: 500;
  color: white;
  height: 2rem;
`;

const ResultListWrapper = styled.div`
  border-bottom: 4px dashed #5c9db2;
  padding: 10px 0 20px;
  margin: 10px 0;
`;
const EditButton = styled(Button)`
  margin-top: 1rem;
  padding: 0 0.5rem;
`;
const TeamTotalsPoints = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 0 0 0.5rem;
`;
const LeftTotal = styled.div`
  width: 50%;
  border-top-left-radius: 0.25rem;
  border-bottom-left-radius: 0.25rem;
  background-color: red;
  height: 1.75rem;
  padding: 0.25rem 1rem;
  text-align: right;
  color: white;
`;

const RightTotal = styled.div`
  width: 50%;
  border-top-right-radius: 0.25rem;
  border-bottom-right-radius: 0.25rem;
  background-color: blue;
  height: 1.75rem;
  padding: 0.25rem 1rem;
  text-align: left;
  color: white;
`;

type TeamGamTournametResultsProps = {
  tournamentIndex: number;
};

export const TeamGameResults: FC<TeamGamTournametResultsProps> = ({ tournamentIndex }): JSX.Element | null => {
  const navigate = useNavigate();
  const [isArchivedTournament, setIsArchivedTournament] = useState(false);
  const { isLoading, error, data } = useFetchTeamGameResultsByDay(tournamentIndex);

  const teamTotals = data as Array<TeamGameMatchDataDisplay>;

  if (teamTotals.length === 0) {
    return null;
  }

  if (isLoading) {
    return <div>Loading...</div>;
  }
  if (error) {
    return <div>Error...</div>;
  }

  return (
    <SectionWrapper data-testid="results">
      <CentreHeading>
        <h2>Results</h2>
      </CentreHeading>
      {teamTotals.map((teamTotal: TeamGameMatchDataDisplay, dayIndex: number) => (
        <ResultListWrapper key={`tour-${tournamentIndex}-result-${dayIndex}`}>
          <Title>
            <div>
              <h3>{`Day: ${dayIndex + 1}`}</h3>
            </div>
            <div>
              <h3>Course: {teamTotal.courseName}</h3>
            </div>
          </Title>
          <Title>
            <h3>{MATCH_FORMATS[teamTotal.matchFormat as 'tierPoints_6_6_6' | 'tierPoints_1_2_3' | 'singles'].name}</h3>
          </Title>
          {(teamTotal.matchFormat === MATCH_FORMATS.tierPoints_6_6_6.id || teamTotal.matchFormat === MATCH_FORMATS.tierPoints_1_2_3.id) && (
            <Fragment>
              {teamTotal.teamGameMatchTeams.map((team, teamIndex) => (
                <TeamGameMatchResult
                  key={`team-${dayIndex}-${teamIndex}`}
                  team={team}
                  teamIndex={teamIndex}
                  startingHole={teamTotal.startingHole}
                  courseId={teamTotal.courseId}
                  matchFormat={teamTotal.matchFormat}
                />
              ))}
            </Fragment>
          )}
          {teamTotal.matchFormat === MATCH_FORMATS.stableford.id && (
            <Fragment>
              {teamTotal.teamGameMatchTeams.map((team, teamIndex) => (
                <TeamGameStablefordResult
                  key={`team-${dayIndex}-${teamIndex}`}
                  team={team}
                  teamIndex={teamIndex}
                  startingHole={teamTotal.startingHole}
                  courseId={teamTotal.courseId}
                  matchFormat={teamTotal.matchFormat}
                />
              ))}
            </Fragment>
          )}
          {(teamTotal.matchFormat === MATCH_FORMATS.singles.id || teamTotal.matchFormat === MATCH_FORMATS.pairs.id) && (
            <Fragment>
              {teamTotal.matchPlayMatches &&
                teamTotal.matchPlayMatches.map((match, index) => (
                  <MatchResult
                    key={`match-${dayIndex}-${index}`}
                    match={match}
                    matchIndex={index}
                    dayIndex={dayIndex}
                    startingHole={teamTotal.startingHole}
                    tourIndex={tournamentIndex}
                    format={'Singles'}
                  />
                ))}
              <h3>Points</h3>
              <TeamTotalsPoints>
                <LeftTotal>{teamTotal.teamGameMatchTeams[0].points}</LeftTotal>
                <RightTotal>{teamTotal.teamGameMatchTeams[1].points}</RightTotal>
              </TeamTotalsPoints>
            </Fragment>
          )}
          <PageWrapper>
            <div>
              <h2>Individual</h2>
            </div>
            <IndividualWrapper>
              <PlayerWrapper>
                <Position></Position>
                <Player></Player>
                <Points>
                  <h4>Points</h4>
                </Points>
                <LdNtp>
                  <h4>Longest Drive</h4>
                </LdNtp>
                <LdNtp>
                  <h4>Nearest The Pin</h4>
                </LdNtp>
              </PlayerWrapper>
            </IndividualWrapper>
            <IndividualWrapper>
              {teamTotal.teamGameMatchIndividualStandings?.map((player: PlayerPoints, index) => (
                <PlayerWrapper key={`individual-${index}-${player.player.nickName}`}>
                  <Position>{player.position}</Position>
                  <Player>{player.player.nickName}</Player>
                  <Position>{player.points}</Position>
                  {player.longestDrive ? <LdNtpSymbol>&#128163;</LdNtpSymbol> : <LdNtpSymbol></LdNtpSymbol>}
                  {player.nearestThePin ? <LdNtpSymbol>&#9971;</LdNtpSymbol> : <LdNtpSymbol></LdNtpSymbol>}
                </PlayerWrapper>
              ))}
            </IndividualWrapper>
          </PageWrapper>
          {isLoggedIn() && !isArchivedTournament && (
            <FlexRowSpaceBetween>
              <EditButton onClick={() => navigate(`/team-game-edit-results/${tournamentIndex}/${dayIndex}`)}>Edit Results</EditButton>
            </FlexRowSpaceBetween>
          )}
        </ResultListWrapper>
      ))}
    </SectionWrapper>
  );
};
